import { useState, useEffect, useRef } from 'react';
import type { SignoffTier } from '../utils/signoff';

const TIER_SIGNED_BG: Record<SignoffTier, string> = {
  Prepared:  'bg-green-600',
  Principal: 'bg-blue-600',
  Final:     'bg-red-600',
};

interface SignoffCheckboxProps {
  tier: SignoffTier;
  isSigned: boolean;
  isClickable: boolean;    // seniority-shield: true = interaction allowed
  title: string;           // tooltip text (pre-computed by parent)
  hasCautionFlag?: boolean; // when true + tier=Principal + isSigned → left-click re-signs
  onSign: () => void;      // called on left-click to sign, or re-sign Principal
  onRemove?: () => void;   // called when context menu "Remove" is confirmed
}

interface ContextMenuPos { x: number; y: number }

export function SignoffCheckbox({
  tier,
  isSigned,
  isClickable,
  title,
  hasCautionFlag = false,
  onSign,
  onRemove,
}: SignoffCheckboxProps) {
  const [isShaking, setIsShaking] = useState(false);
  const [isPopping, setIsPopping] = useState(false);
  const [menu, setMenu] = useState<ContextMenuPos | null>(null);
  const prevSignedRef = useRef(isSigned);
  const menuRef = useRef<HTMLDivElement>(null);

  // Pop animation on unsigned → signed transition
  useEffect(() => {
    if (!prevSignedRef.current && isSigned) {
      setIsPopping(true);
      const t = setTimeout(() => setIsPopping(false), 200);
      return () => clearTimeout(t);
    }
    prevSignedRef.current = isSigned;
  }, [isSigned]);

  // Close context menu on outside click or Escape
  useEffect(() => {
    if (!menu) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenu(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menu]);

  const handleClick = () => {
    // Blocked tier: shake and bail
    if (!isClickable && !isSigned) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 400);
      return;
    }
    // Signed Principal with caution flag: re-sign
    if (isSigned && tier === 'Principal' && hasCautionFlag) {
      onSign();
      return;
    }
    // Already signed (non-re-sign): guide user to right-click, no shake
    if (isSigned) return;
    // Sign
    onSign();
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!isSigned) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };

  const handleRemoveConfirm = () => {
    onRemove?.();
    setMenu(null);
  };

  return (
    <>
      <button
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        title={title}
        style={isShaking ? { animation: 'shake 0.4s ease-in-out' } : undefined}
        className={`
          inline-flex items-center justify-center
          w-6 h-6 rounded-md text-xs font-bold
          transition-transform duration-200
          ${isPopping ? 'scale-110' : 'scale-100'}
          ${isSigned
            ? `${TIER_SIGNED_BG[tier]} text-white`
            : isClickable
              ? 'border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-400 hover:border-slate-400 dark:hover:border-slate-500 cursor-pointer'
              : 'border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-40'
          }
        `}
      >
        {isSigned ? 'CPA' : ''}
      </button>

      {menu && (
        <div
          ref={menuRef}
          style={{ top: menu.y, left: menu.x }}
          className="fixed z-50 backdrop-blur-md bg-white/80 dark:bg-neutral-900/80 border border-white/20 dark:border-neutral-700/40 rounded-lg shadow-xl p-1 min-w-[190px]"
        >
          <button
            onClick={handleRemoveConfirm}
            disabled={!isClickable}
            title={!isClickable ? title : undefined}
            className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Remove {tier} Sign-off?
          </button>
        </div>
      )}
    </>
  );
}
